/**
 * Every school on the platform, and the two things only we can do: create one, and
 * put a licence on it.
 *
 * Creating a school is platform-admin only by design — minors' data means a human
 * verifies an institution before it gets a roster. The principal is invited by email
 * in the same call and becomes an `admin` member the first time they log in.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Building2, Plus } from 'lucide-react'
import type { SchoolAdminSummary } from '@modulus/api'
import { EmailList } from '../components/EmailList'
import { Card, Pill, SectionTitle } from '../components/ui'
import { EmptyState, ErrorBlock, RefreshButton, SkeletonRows } from '../components/states'
import { Field, FormError, GhostButton, PrimaryButton, TextInput } from '../components/form'
import { useResource } from '../lib/useResource'
import { api } from '../lib/api'
import { useStepUp } from '../components/StepUp'

/** `active` is the unremarkable one; the others are worth noticing at a glance. */
const STATUS_COLOURS: Record<string, string> = {
  active: 'var(--awarded)',
  suspended: 'var(--warn)',
  archived: 'var(--muted)',
}

function StatusPill({ status }: { status: string }) {
  return <Pill color={STATUS_COLOURS[status] ?? 'var(--muted)'}>{status}</Pill>
}

/** Null seat limit means unlimited — the honest answer for a pilot, an uncapped
 *  licence and a lapsed one alike. Never render it as zero. */
function seats(school: SchoolAdminSummary): string {
  return school.seatLimit === null
    ? `${school.seatsUsed} of unlimited`
    : `${school.seatsUsed} of ${school.seatLimit}`
}

export function Schools() {
  const schools = useResource<SchoolAdminSummary[]>('/admin/schools')
  const [creating, setCreating] = useState(false)

  return (
    <div className="animate-fade-up space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle title="Schools" subtitle="Every institution on the platform." />
        <div className="flex items-center gap-2">
          <RefreshButton onClick={schools.refresh} busy={schools.loading} />
          <PrimaryButton onClick={() => setCreating((open) => !open)}>
            <Plus size={16} />
            New school
          </PrimaryButton>
        </div>
      </div>

      {creating && (
        <CreateSchool
          onDone={() => {
            setCreating(false)
            schools.refresh()
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {schools.error && <ErrorBlock error={schools.error} onRetry={schools.refresh} />}
      {schools.loading && !schools.data && <SkeletonRows rows={5} />}

      {schools.data?.length === 0 && (
        <EmptyState
          icon={<Building2 size={22} />}
          title="No schools yet"
          body="Create one to invite its principal. They become an admin the first time they log in."
        />
      )}

      {schools.data && schools.data.length > 0 && (
        <Card className="!p-0">
          {/* The table scrolls inside the card; the page never scrolls sideways. */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-faint">
                  <th className="px-4 py-3 font-semibold">School</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Students</th>
                  <th className="px-4 py-3 font-semibold">Staff</th>
                  <th className="px-4 py-3 font-semibold">Seats</th>
                </tr>
              </thead>
              <tbody>
                {schools.data.map((school) => (
                  <tr key={school.id} className="border-t border-hairline hover:bg-surface">
                    <td className="px-4 py-3">
                      <Link
                        to={`/schools/${school.id}`}
                        className="font-semibold text-ink hover:text-accent"
                      >
                        {school.name}
                      </Link>
                      <p className="text-xs text-faint">
                        {[school.countryCode, school.timezone].filter(Boolean).join(' · ') || '—'}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={school.status} />
                    </td>
                    <td className="px-4 py-3 tabular-nums">{school.members.students}</td>
                    <td className="px-4 py-3 tabular-nums">
                      {school.members.teachers + school.members.admins}
                    </td>
                    <td className="px-4 py-3 tabular-nums">{seats(school)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}

function CreateSchool({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState('')
  const [adminEmails, setAdminEmails] = useState<string[]>([])
  const [countryCode, setCountryCode] = useState('')
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const stepUp = useStepUp()

  const submit = async () => {
    setError(null)

    // Creating a school provisions access to children's data. Confirming identity
    // again costs one password and removes a whole class of "someone used my open
    // laptop" from the incident list.
    const confirmed = await stepUp('Create a school and invite its admins')
    if (!confirmed) return

    setBusy(true)
    try {
      await api.platform.createSchool({
        name: name.trim(),
        adminEmails,
        countryCode: countryCode.trim() || null,
        timezone: timezone.trim() || null,
      })
      onDone()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create the school.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <div className="space-y-4">
        <SectionTitle
          title="New school"
          subtitle="Admins are invited by email and become admins on first login. They can then add teachers and students themselves."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="St Andrew's College" />
          </Field>
          <div className="sm:col-span-2">
            <Field
              label="School admins"
              hint="One or more. Paste a list — commas, spaces and line breaks all split. Nothing is emailed; the membership binds when they first sign in."
            >
              <EmailList
                emails={adminEmails}
                onChange={setAdminEmails}
                placeholder="head@school.edu"
              />
            </Field>
          </div>
          <Field label="Country code" hint="ISO 3166-1 alpha-2, e.g. IE. Optional.">
            <TextInput value={countryCode} onChange={(e) => setCountryCode(e.target.value)} maxLength={2} />
          </Field>
          <Field label="Timezone" hint="Defaults to yours. The school can change it.">
            <TextInput value={timezone} onChange={(e) => setTimezone(e.target.value)} />
          </Field>
        </div>

        <FormError message={error} />

        <div className="flex gap-2">
          <PrimaryButton
            onClick={() => void submit()}
            disabled={busy || !name.trim() || adminEmails.length === 0}
          >
            {busy ? 'Creating…' : 'Create school'}
          </PrimaryButton>
          <GhostButton onClick={onCancel}>Cancel</GhostButton>
        </div>
      </div>
    </Card>
  )
}
